import * as WebBrowser from 'expo-web-browser';
import { APP_ROUTES } from '@/navigators/routes';
import { NavigationState as BaseNavigationState } from '@react-navigation/native';
import { Linking, Platform } from 'react-native';

export type NavigationState =
  | BaseNavigationState
  | Partial<BaseNavigationState>
  | undefined;
export type NavigationStateRoute = BaseNavigationState['routes'][0] | undefined;

export const getActiveRoute = (
  state: NavigationState,
  parentStackRoutes?: Set<string>,
): NavigationStateRoute => {
  const route = state?.routes?.[state?.index || 0];

  if (parentStackRoutes && route?.name && parentStackRoutes.has(route.name)) {
    return route;
  }

  if (route?.state) {
    // @ts-ignore
    return getActiveRoute(route.state);
  }

  return route;
};

export const getActiveRouteName = (
  state: NavigationState,
  parentStackRoutes?: Set<string>,
): string => {
  const activeRoute = getActiveRoute(state, parentStackRoutes);
  return activeRoute?.name || '';
};

type ResolvableURL = string | (() => Promise<string>);

export const openPDF = async (pdfURL: ResolvableURL) => {
  const getViewerURL = (url: string) => {
    // The default browser on Android doesn't have a built-in
    // PDF viewer so we load it through Google Docs
    // https://stackoverflow.com/a/43266525
    return Platform.OS === 'android'
      ? `https://docs.google.com/viewerng/viewer?url=${url}`
      : url;
  };

  return await openWebBrowserURL(
    typeof pdfURL === 'function'
      ? async () => getViewerURL(await pdfURL())
      : getViewerURL(pdfURL),
  );
};

/**
 * Opens a URL based on the platform, encoding the URL if necessary.
 *
 * @param url - The URL to open, which can be a string or an async function that returns a string.
 * @returns A promise that resolves when the URL is opened.
 */
export const openWebBrowserURL = async (url: ResolvableURL) => {
  let browserWindow: Window | null = null;

  let resolvedURL: string;
  if (typeof url === 'function') {
    if (Platform.OS === 'web') {
      // On web, open a window with a loading page to prevent the browser from blocking
      // the window from opening if the URL takes a while to resolve
      browserWindow = window.open('/loading', '_blank');
      if (!browserWindow) {
        throw new Error('Failed to open browser window');
      }
    }

    try {
      resolvedURL = await url();
    } catch (e) {
      if (Platform.OS === 'web' && browserWindow) {
        browserWindow.close();
      }

      throw e;
    }
  } else {
    resolvedURL = url;
  }

  const isAlreadyEncoded = resolvedURL !== decodeURIComponent(resolvedURL);
  const encodedURL = isAlreadyEncoded ? resolvedURL : encodeURI(resolvedURL);

  if (Platform.OS === 'web') {
    if (browserWindow) {
      browserWindow.location.href = encodedURL;
    } else {
      await Linking.openURL(encodedURL);
    }

    return;
  }

  await WebBrowser.openBrowserAsync(encodedURL, {
    // Android only
    enableDefaultShareMenuItem: true,
  });
};

export const NAVIGATION_TAB_LABELS: { [key: string]: string } = {
  [APP_ROUTES.Account]: 'my_account',
  [APP_ROUTES.Orders]: 'orders',
  [APP_ROUTES.Catalog]: 'catalog',
  [APP_ROUTES.Chat]: 'chat',
  [APP_ROUTES.Links]: 'links',
  [APP_ROUTES.MenuInfo]: 'menu',
  [APP_ROUTES.OrderGuide]: 'order_guide',
  [APP_ROUTES.Payments]: 'payments',
  // This route's label is "Employees" for now but RestaurantStack is going to
  // have more functionality in the future
  [APP_ROUTES.Restaurant]: 'employees',
};

export const isWeb = Platform.OS === 'web';
